import { panelStorageKey } from '../store/namespace';
import { PANEL_EVENTS } from './protocol';
import { useState, useEffect, useCallback, useRef } from 'react';
import { TabConfig, PanelGroup, FloatingWindow } from './types';
import { generateUniqueId, getFromStorage } from './utils';
import {
    markPanelHidden,
    markPanelVisible,
    isPanelHidden,
} from './panelVisibilityFlags';
import { getPanelRegistry } from '../registry/createPanelRegistry';

/** Canonical panel id via the package registry (unknown ids pass through). */
const toCanonicalPanelId = (id: string): string =>
    getPanelRegistry()?.toCanonicalId(id) ?? id;
/** Collapsed/minimized state is per panel instance: several permanent
 * shells (e.g. a right column plus a bottom dock) must not overwrite each
 * other's state. The primary instance keeps the legacy unprefixed keys so
 * existing persisted layouts survive untouched. */
const panelCollapsedKey = (panelId?: string) =>
    panelId && panelId !== 'main-panel'
        ? panelStorageKey(`sidePanel.collapsed.${panelId}`)
        : panelStorageKey('sidePanel.collapsed');
const panelMinimizedKey = (panelId?: string) =>
    panelId && panelId !== 'main-panel'
        ? panelStorageKey(`sidePanel.minimized.${panelId}`)
        : panelStorageKey('sidePanel.minimized');
const panelGroupDimensionsKey = () => panelStorageKey('sidePanel.groupDimensions');
const panelHorizontalWidthKey = () => panelStorageKey('sidePanel.horizontalWidth');
const panelVerticalHeightKey = () => panelStorageKey('sidePanel.verticalHeight');
type PanelPosition = 'left' | 'right' | 'top' | 'bottom';
type GroupDimensionsMap = Record<PanelPosition, Record<string, number>>;
export interface UseSidePanelOptions {
    panelId?: string;
    initialWidth: number;
    initialPanelGroups: PanelGroup[];
    minWidth: number;
    defaultWidth: number;
    minHeight: number;
    defaultHeight: number;
    collapsedWidth: number;
    collapsedHeight?: number;
    panelPosition?: PanelPosition;
    setPanelGroups?: React.Dispatch<React.SetStateAction<PanelGroup[]>>;
    onRemovePanel?: (panelId: string) => void;
    floatingWindows?: FloatingWindow[];
    setFloatingWindows?: React.Dispatch<React.SetStateAction<FloatingWindow[]>>;
}
export function useSidePanel({
    panelId = 'main-panel',
    initialWidth,
    initialPanelGroups,
    minWidth,
    defaultWidth,
    minHeight,
    defaultHeight,
    collapsedWidth,
    collapsedHeight,
    panelPosition = 'left',
    setPanelGroups: setPanelGroupsExternal,
    onRemovePanel,
    floatingWindows: floatingWindowsExternal = [],
    setFloatingWindows: setFloatingWindowsExternal,
}: UseSidePanelOptions) {
    const [hydrating, setHydrating] = useState(true);
    const [isClient, setIsClient] = useState(false);
    const [width, setWidth] = useState<number>(initialWidth);
    const [lastWidth, setLastWidth] = useState<number>(initialWidth);
    const [height, setHeight] = useState<number>(defaultHeight);
    const [lastHeight, setLastHeight] = useState<number>(defaultHeight);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const [isMinimized, setIsMinimized] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>(() => {
        if (
            initialPanelGroups.length > 0 &&
            initialPanelGroups[0].tabs.length > 0
        ) {
            return initialPanelGroups[0].tabs[0].id;
        }
        return '';
    });
    const [isResizing, setIsResizing] = useState(false);
    const [activeDropZone, setActiveDropZone] = useState<string | null>(null);
    const [isDraggingOutside, setIsDraggingOutside] = useState(false);
    const [draggedTab, setDraggedTab] = useState<{
        tabId: string;
        groupId: string;
    } | null>(null);
    const AUTO_MINIMIZE_THRESHOLD = 50;
    const [panelGroupsState, setPanelGroupsState] = useState<PanelGroup[]>(
        initialPanelGroups.map((group) => ({
            ...group,
            isMinimized: group.isMinimized ?? false,
        })),
    );
    const panelGroups = setPanelGroupsExternal
        ? initialPanelGroups
        : panelGroupsState;
    const setPanelGroups = setPanelGroupsExternal || setPanelGroupsState;
    const [groupDimensions, setGroupDimensions] = useState<GroupDimensionsMap>(
        {} as GroupDimensionsMap,
    );
    const isHorizontal = panelPosition === 'top' || panelPosition === 'bottom';
    const [floatingWindowsState, setFloatingWindowsState] = useState<
        FloatingWindow[]
    >(floatingWindowsExternal);
    const floatingWindows = setFloatingWindowsExternal
        ? floatingWindowsExternal
        : floatingWindowsState;
    const setFloatingWindows =
        setFloatingWindowsExternal || setFloatingWindowsState;
    function getRestoredWidth(): number {
        const stored = getFromStorage(panelHorizontalWidthKey(), initialWidth);
        if (stored === collapsedWidth) {
            return lastWidth || initialWidth;
        }
        return stored;
    }
    function getRestoredHeight(): number {
        const stored = getFromStorage(panelVerticalHeightKey(), defaultHeight);
        const minCollapsed = collapsedHeight ?? collapsedWidth;
        if (stored === minCollapsed) {
            return lastHeight || defaultHeight;
        }
        return stored;
    }
    useEffect(() => {
        setIsClient(true);
        setTimeout(() => {
            try {
                setIsCollapsed(getFromStorage(panelCollapsedKey(panelId), false));
                setIsMinimized(getFromStorage(panelMinimizedKey(panelId), false));
                setGroupDimensions(
                    getFromStorage(
                        panelGroupDimensionsKey(),
                        {},
                    ) as GroupDimensionsMap,
                );
                if (panelPosition === 'left' || panelPosition === 'right') {
                    setWidth(
                        getFromStorage(
                            panelHorizontalWidthKey(),
                            initialWidth,
                        ),
                    );
                } else {
                    setHeight(
                        getFromStorage(
                            panelVerticalHeightKey(),
                            defaultHeight,
                        ),
                    );
                }
            } catch {}
            setHydrating(false);
        }, 0);
    }, []);
    useEffect(() => {
        if (!hydrating) {
            try {
                if (typeof window !== 'undefined') {
                    localStorage.setItem(
                        panelCollapsedKey(panelId),
                        String(isCollapsed),
                    );
                }
            } catch {}
        }
    }, [isCollapsed, hydrating]);
    useEffect(() => {
        if (!hydrating) {
            try {
                if (!isCollapsed) {
                    if (panelPosition === 'left' || panelPosition === 'right') {
                        localStorage.setItem(
                            panelHorizontalWidthKey(),
                            String(width),
                        );
                    } else {
                        localStorage.setItem(
                            panelVerticalHeightKey(),
                            String(height),
                        );
                    }
                }
            } catch {}
        }
    }, [width, height, isCollapsed, hydrating, panelPosition]);
    useEffect(() => {
        if (!hydrating) {
            try {
                if (typeof window !== 'undefined') {
                    localStorage.setItem(
                        panelMinimizedKey(panelId),
                        String(isMinimized),
                    );
                }
            } catch {}
        }
    }, [isMinimized, hydrating]);

    // ── Window bounds clamping ──────────────────────────────────────────────
    // The docked panel is constrained horizontally and vertically by the app
    // window: it can never exceed the window's inner width/height. Applied on
    // manual resizes and whenever the window itself is resized.
    const getViewportSize = useCallback(() => {
        if (typeof window === 'undefined') {
            return { width: 1920, height: 1080 };
        }
        return {
            width: window.innerWidth,
            height: window.innerHeight,
        };
    }, []);

    useEffect(() => {
        if (hydrating) return;
        if (panelPosition === 'left' || panelPosition === 'right') {
            const maxW = Math.max(minWidth, getViewportSize().width);
            if (width > maxW) setWidth(maxW);
        } else {
            const maxH = Math.max(minHeight, getViewportSize().height);
            if (height > maxH) setHeight(maxH);
        }
    }, [
        hydrating,
        panelPosition,
        width,
        height,
        minWidth,
        minHeight,
        getViewportSize,
    ]);

    useEffect(() => {
        if (hydrating) return;
        const handleWindowResize = () => {
            const { width: vw, height: vh } = getViewportSize();
            if (panelPosition === 'left' || panelPosition === 'right') {
                setWidth((prev) =>
                    Math.min(Math.max(minWidth, prev), Math.max(minWidth, vw)),
                );
            } else {
                setHeight((prev) =>
                    Math.min(
                        Math.max(minHeight, prev),
                        Math.max(minHeight, vh),
                    ),
                );
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [hydrating, panelPosition, minWidth, minHeight, getViewportSize]);
    useEffect(() => {
        if (hydrating || isResizing) return;

        const currentSize = isHorizontal ? height : width;

        if (currentSize <= AUTO_MINIMIZE_THRESHOLD && !isMinimized) {
            setLastWidth(width);
            setLastHeight(height);
            setIsMinimized(true);
            if (isHorizontal) {
                setHeight(AUTO_MINIMIZE_THRESHOLD);
            } else {
                setWidth(AUTO_MINIMIZE_THRESHOLD);
            }
        }
    }, [
        hydrating,
        isHorizontal,
        isMinimized,
        width,
        height,
        isResizing,
    ]);
    useEffect(() => {
        if (!hydrating) {
            setGroupDimensions((prev) => {
                const groupHeights: Record<string, number> = {};
                (panelGroups ?? []).forEach((g) => {
                    if (g.height) groupHeights[g.id] = g.height;
                });
                const next: GroupDimensionsMap = {
                    ...prev,
                    [panelPosition]: groupHeights,
                };
                try {
                    localStorage.setItem(
                        panelGroupDimensionsKey(),
                        JSON.stringify(next),
                    );
                } catch {}
                return next;
            });
        }
    }, [panelGroups, panelPosition, hydrating]);
    const lastPanelPositionRef = useRef<PanelPosition>(panelPosition);
    useEffect(() => {
        if (!hydrating) {
            const lastPanelPosition = lastPanelPositionRef.current;
            if (lastPanelPosition !== panelPosition) {
                if (panelPosition === 'left' || panelPosition === 'right') {
                    setWidth(getRestoredWidth());
                } else {
                    setHeight(getRestoredHeight());
                }
                const groupDims = getFromStorage(
                    panelGroupDimensionsKey(),
                    {},
                ) as GroupDimensionsMap;
                const posKey = panelPosition as PanelPosition;
                if (groupDims[posKey]) {
                    setPanelGroups((prev) =>
                        prev.map((group) =>
                            groupDims[posKey][group.id] !== undefined
                                ? {
                                      ...group,
                                      height: groupDims[posKey][group.id],
                                  }
                                : group,
                        ),
                    );
                }
                lastPanelPositionRef.current = panelPosition;
            }
        }
    }, [
        panelPosition,
        hydrating,
        lastWidth,
        lastHeight,
        collapsedWidth,
        collapsedHeight,
    ]);
    const toggleCollapse = () => {
        if (!isCollapsed) {
            if (isHorizontal) {
                setLastHeight(height);
                setHeight(collapsedHeight ?? collapsedWidth);
            } else {
                setLastWidth(width);
                setWidth(collapsedWidth);
            }
            setIsCollapsed(true);
        } else {
            setIsCollapsed(false);
            if (isHorizontal) {
                setHeight(lastHeight || defaultHeight);
            } else {
                setWidth(lastWidth || defaultWidth);
            }
        }
    };
    const toggleMinimized = () => {
        setIsMinimized((prev) => {
            if (prev) {
                if (isHorizontal) {
                    setHeight(
                        lastHeight < minHeight ? defaultHeight : lastHeight,
                    );
                } else {
                    setWidth(lastWidth < minWidth ? defaultWidth : lastWidth);
                }
            } else {
                if (isHorizontal) {
                    setLastHeight(height);
                } else {
                    setLastWidth(width);
                }
            }
            return !prev;
        });
    };
    // ── Native resize: Ana panel (Direct DOM Mutation / Zero-Rerender 120 FPS) ──
    const createPanelResizeHandleMouseDown =
        (
            position: PanelPosition,
            panelEl?: React.RefObject<HTMLElement | null>,
        ) =>
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            document.body.classList.add('panel-resizing');
            const startX = e.clientX;
            const startY = e.clientY;
            const startWidth = width;
            const startHeight = height;
            const startMinimized = isMinimized;

            let latestWidth = startWidth;
            let latestHeight = startHeight;
            let rafId: number | null = null;
            let dragMinimizedState = startMinimized;
            const targetEl = panelEl?.current;
            // A plain click (no drag beyond the threshold) toggles icon/panel
            // mode; any real movement switches to resize behavior.
            const DRAG_THRESHOLD = 3;
            let dragged = false;

            const onMouseMove = (ev: MouseEvent) => {
                if (!dragged) {
                    if (
                        Math.max(
                            Math.abs(ev.clientX - startX),
                            Math.abs(ev.clientY - startY),
                        ) <= DRAG_THRESHOLD
                    ) {
                        return;
                    }
                    dragged = true;
                }

                const applyThresholdSnap = (nextSize: number) => {
                    if (dragMinimizedState) {
                        if (nextSize >= AUTO_MINIMIZE_THRESHOLD) {
                            setIsMinimized(false);
                            dragMinimizedState = false;
                            return nextSize;
                        }

                        setIsMinimized(true);
                        dragMinimizedState = true;
                        return AUTO_MINIMIZE_THRESHOLD;
                    }

                    if (nextSize <= AUTO_MINIMIZE_THRESHOLD) {
                        setIsMinimized(true);
                        dragMinimizedState = true;
                        return AUTO_MINIMIZE_THRESHOLD;
                    }

                    return nextSize;
                };

                if (position === 'left') {
                    const delta = ev.clientX - startX;
                    const maxW = Math.max(minWidth, getViewportSize().width);
                    latestWidth = Math.min(
                        maxW,
                        Math.max(collapsedWidth, startWidth + delta),
                    );
                    latestWidth = applyThresholdSnap(latestWidth);
                } else if (position === 'right') {
                    const delta = startX - ev.clientX;
                    const maxW = Math.max(minWidth, getViewportSize().width);
                    latestWidth = Math.min(
                        maxW,
                        Math.max(collapsedWidth, startWidth + delta),
                    );
                    latestWidth = applyThresholdSnap(latestWidth);
                } else if (position === 'top') {
                    const delta = ev.clientY - startY;
                    const maxH = Math.max(minHeight, getViewportSize().height);
                    latestHeight = Math.min(
                        maxH,
                        Math.max(
                            collapsedHeight ?? collapsedWidth,
                            startHeight + delta,
                        ),
                    );
                    latestHeight = applyThresholdSnap(latestHeight);
                } else if (position === 'bottom') {
                    const delta = startY - ev.clientY;
                    const maxH = Math.max(minHeight, getViewportSize().height);
                    latestHeight = Math.min(
                        maxH,
                        Math.max(
                            collapsedHeight ?? collapsedWidth,
                            startHeight + delta,
                        ),
                    );
                    latestHeight = applyThresholdSnap(latestHeight);
                }

                if (rafId === null) {
                    rafId = requestAnimationFrame(() => {
                        rafId = null;
                        if (position === 'left' || position === 'right') {
                            if (targetEl) {
                                targetEl.style.width = `${latestWidth}px`;
                            }
                            setWidth(latestWidth);
                        } else {
                            if (targetEl) {
                                targetEl.style.height = `${latestHeight}px`;
                            }
                            setHeight(latestHeight);
                        }
                    });
                }
            };

            const onMouseUp = () => {
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                setIsResizing(false);
                document.body.classList.remove('panel-resizing');
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);

                // No drag happened → single-click toggle between icon/panel mode
                if (!dragged) {
                    toggleMinimized();
                    return;
                }

                // Commit final size to React state and localStorage once on mouse up
                if (position === 'left' || position === 'right') {
                    const snappedWidth =
                        latestWidth <= AUTO_MINIMIZE_THRESHOLD
                            ? AUTO_MINIMIZE_THRESHOLD
                            : latestWidth;
                    const shouldBeMinimized = snappedWidth <= AUTO_MINIMIZE_THRESHOLD;
                    setWidth(snappedWidth);
                    setIsMinimized(shouldBeMinimized);
                    try {
                        localStorage.setItem(
                            panelHorizontalWidthKey(),
                            String(snappedWidth),
                        );
                    } catch {}
                } else {
                    const snappedHeight =
                        latestHeight <= AUTO_MINIMIZE_THRESHOLD
                            ? AUTO_MINIMIZE_THRESHOLD
                            : latestHeight;
                    const shouldBeMinimized = snappedHeight <= AUTO_MINIMIZE_THRESHOLD;
                    setHeight(snappedHeight);
                    setIsMinimized(shouldBeMinimized);
                    try {
                        localStorage.setItem(
                            panelVerticalHeightKey(),
                            String(snappedHeight),
                        );
                    } catch {}
                }
            };

            window.addEventListener('mousemove', onMouseMove, {
                passive: true,
            });
            window.addEventListener('mouseup', onMouseUp);
        };

    // ── Native resize: Sub-group (Zero-React-Rerender / Direct DOM Mutation) ───
    // During drag: write style.height directly on the DOM element → 0 React
    // re-renders. isLastGroup + panelBottom are pre-computed at mousedown so
    // ── Native resize: Cascade Accordion Splitter (Zero-React-Rerender) ───
    // Multi-panel cascade push-pull:
    // - Dragging downwards expands group A, pushing down subsequent groups.
    // - Subsequent groups shrink until they reach minHeight (90px).
    // - Once all reach minHeight, groups auto-collapse starting from the bottom-most group.
    // - Pulling upwards restores groups like an accordion, returning to remembered saved heights.
    const createGroupResizeHandleMouseDown =
        (
            groupId: string,
            currentHeight: number,
            panelEl?: React.RefObject<HTMLElement | null>,
        ) =>
        (e: React.MouseEvent) => {
            e.preventDefault();
            document.body.classList.add('panel-resizing');

            const startY = e.clientY;
            const visibleAtStart = (panelGroups ?? []).filter(
                (g) => g.isVisible,
            );
            const activeIdx = visibleAtStart.findIndex((g) => g.id === groupId);
            if (activeIdx < 0 || activeIdx >= visibleAtStart.length - 1) {
                document.body.classList.remove('panel-resizing');
                return;
            }

            const headerH = 36;
            const minH = minHeight || 90;

            // Take snapshots of all visible groups
            const snapshots = visibleAtStart.map((g) => {
                const el = panelEl?.current?.querySelector(
                    `[data-group-id="${g.id}"]`,
                ) as HTMLElement | null;
                const renderedH =
                    el?.offsetHeight ||
                    (g.isMinimized ? headerH : g.height || minH);
                const rememberedH =
                    g.previousHeight && g.previousHeight >= minH
                        ? g.previousHeight
                        : g.height && g.height >= minH
                          ? g.height
                          : 200;

                return {
                    id: g.id,
                    el,
                    startHeight: renderedH,
                    savedHeight: rememberedH,
                    isMinimized: !!g.isMinimized,
                };
            });

            // Total height of active group and everything below it remains 100% constant
            const totalSubH = snapshots
                .slice(activeIdx)
                .reduce((sum, s) => sum + s.startHeight, 0);

            // Calculation function for cascade accordion
            const computeCascade = (delta: number) => {
                const res = snapshots.map((s) => ({
                    id: s.id,
                    height: s.startHeight,
                    isMinimized: s.isMinimized,
                }));

                const n = snapshots.length;

                if (delta >= 0) {
                    // ── PUSH DOWNWARDS ──
                    let remainingPush = delta;

                    // 1) Shrink groups below activeIdx down to minH (90px) top-down
                    for (let i = activeIdx + 1; i < n; i++) {
                        if (remainingPush <= 0) break;
                        if (res[i].isMinimized) continue;
                        const canShrink = Math.max(0, res[i].height - minH);
                        const shrink = Math.min(canShrink, remainingPush);
                        res[i].height -= shrink;
                        remainingPush -= shrink;
                    }

                    // 2) If still pushing, auto-collapse from the bottom-most group upwards
                    if (remainingPush > 0) {
                        for (let i = n - 1; i > activeIdx; i--) {
                            if (remainingPush <= 0) break;
                            if (res[i].isMinimized) continue;
                            const gain = res[i].height - headerH;
                            res[i].isMinimized = true;
                            res[i].height = headerH;
                            remainingPush -= gain;
                        }
                    }

                    // Active group absorbs the freed space (totalSubH constant)
                    const belowSum = res
                        .slice(activeIdx + 1)
                        .reduce((sum, r) => sum + r.height, 0);
                    res[activeIdx].height = totalSubH - belowSum;
                } else {
                    // ── PULL UPWARDS (RE-EXPAND ACCORDION) ──
                    const maxShrinkA = Math.max(
                        0,
                        snapshots[activeIdx].startHeight - minH,
                    );
                    const actualShrinkA = Math.min(maxShrinkA, Math.abs(delta));
                    res[activeIdx].height =
                        snapshots[activeIdx].startHeight - actualShrinkA;

                    let spaceToRestore = actualShrinkA;

                    // Expand groups below activeIdx back toward their remembered savedHeight
                    for (let i = activeIdx + 1; i < n; i++) {
                        if (spaceToRestore <= 0) break;
                        const targetSaved = Math.max(
                            minH,
                            snapshots[i].savedHeight,
                        );

                        if (res[i].isMinimized) {
                            const neededToOpen = minH - headerH;
                            if (spaceToRestore >= neededToOpen) {
                                res[i].isMinimized = false;
                                res[i].height = minH;
                                spaceToRestore -= neededToOpen;
                            } else {
                                break;
                            }
                        }

                        if (!res[i].isMinimized) {
                            const canGrow = Math.max(
                                0,
                                targetSaved - res[i].height,
                            );
                            const grow = Math.min(canGrow, spaceToRestore);
                            res[i].height += grow;
                            spaceToRestore -= grow;
                        }
                    }

                    // Any surplus given to the bottom-most open group to keep exact flush fit
                    if (spaceToRestore > 0) {
                        const openBelow = res
                            .slice(activeIdx + 1)
                            .filter((r) => !r.isMinimized);
                        const lastOpen = openBelow[openBelow.length - 1];
                        if (lastOpen) {
                            lastOpen.height += spaceToRestore;
                        } else {
                            res[activeIdx].height += spaceToRestore;
                        }
                    }
                }

                return res;
            };

            let latestRes = computeCascade(0);
            let rafId: number | null = null;

            const onMouseMove = (ev: MouseEvent) => {
                const delta = ev.clientY - startY;
                latestRes = computeCascade(delta);

                if (rafId === null) {
                    rafId = requestAnimationFrame(() => {
                        rafId = null;
                        for (let i = activeIdx; i < snapshots.length; i++) {
                            const item = latestRes[i];
                            const el = snapshots[i].el;
                            if (el) {
                                el.style.height = `${item.height}px`;
                                if (item.isMinimized) {
                                    el.classList.add('minimized');
                                } else {
                                    el.classList.remove('minimized');
                                }
                            }
                        }
                    });
                }
            };

            const onMouseUp = () => {
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }

                // Clear inline style mutations
                for (let i = activeIdx; i < snapshots.length; i++) {
                    const el = snapshots[i].el;
                    if (el) {
                        el.style.height = '';
                    }
                }

                document.body.classList.remove('panel-resizing');
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);

                // Commit cascade changes to React state
                const resMap = new Map(latestRes.map((r) => [r.id, r]));

                setPanelGroups((prev) =>
                    prev.map((group) => {
                        const updated = resMap.get(group.id);
                        if (!updated) return group;

                        const snap = snapshots.find((s) => s.id === group.id);
                        const isNowMinimized = updated.isMinimized;
                        const wasMinimizedBefore = snap?.isMinimized ?? false;

                        // Remember position when auto-collapsing
                        let prevH = group.previousHeight;
                        if (isNowMinimized && !wasMinimizedBefore) {
                            prevH =
                                snap?.savedHeight ||
                                snap?.startHeight ||
                                group.height;
                        }

                        return {
                            ...group,
                            height: updated.height,
                            isMinimized: isNowMinimized,
                            isAutoCollapsed: isNowMinimized,
                            previousHeight: prevH,
                            isCustomHeight:
                                group.id === groupId
                                    ? true
                                    : group.isCustomHeight,
                        };
                    }),
                );

                setGroupDimensions((prev) => {
                    const groupHeights = { ...(prev[panelPosition] || {}) };
                    latestRes.forEach((r) => {
                        if (!r.isMinimized) {
                            groupHeights[r.id] = r.height;
                        }
                    });
                    const next = { ...prev, [panelPosition]: groupHeights };
                    try {
                        localStorage.setItem(
                            panelGroupDimensionsKey(),
                            JSON.stringify(next),
                        );
                    } catch {}
                    return next;
                });
            };

            window.addEventListener('mousemove', onMouseMove, {
                passive: true,
            });
            window.addEventListener('mouseup', onMouseUp);
        };
    const activeTabRef = useRef(activeTab);
    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    const isCollapsedRef = useRef(isCollapsed);
    useEffect(() => {
        isCollapsedRef.current = isCollapsed;
    }, [isCollapsed]);

    const isMinimizedRef = useRef(isMinimized);
    useEffect(() => {
        isMinimizedRef.current = isMinimized;
    }, [isMinimized]);

    // Live snapshot of panelGroups for event handlers registered once on
    // mount; without it handleActivatePanel would read the mount-time groups
    // forever and re-dispatch requestRestoreTab for tabs added later,
    // producing an endless restore loop.
    const panelGroupsRef = useRef(panelGroups);
    useEffect(() => {
        panelGroupsRef.current = panelGroups;
    }, [panelGroups]);

    const TAB_ALIASES: Record<string, string[]> = {
        history: ['history'],
        properties: ['properties'],
        brushes: ['brushes-panel', 'brushes'],
        'brushes-panel': ['brushes-panel', 'brushes'],
        'brush-settings': ['brush-settings-panel', 'brush-settings'],
        'brush-settings-panel': ['brush-settings-panel', 'brush-settings'],
        swatches: ['swatches-panel', 'swatches'],
        'swatches-panel': ['swatches-panel', 'swatches'],
        colors: ['color-panel', 'colors'],
        'color-panel': ['color-panel', 'colors'],
        'color-harmony': ['color-harmony'],
        gradient: ['gradient-panel', 'gradient'],
        'gradient-panel': ['gradient-panel', 'gradient'],
        pattern: ['pattern-panel', 'pattern'],
        'pattern-panel': ['pattern-panel', 'pattern'],
        'color-settings': ['color-settings'],
        'soft-proofing': ['soft-proofing'],
        layers: ['layers-panel', 'layers'],
        'layers-panel': ['layers-panel', 'layers'],
        channels: ['channels'],
        pages: ['pages-panel', 'pages'],
        'pages-panel': ['pages-panel', 'pages'],
        boards: ['boards-panel', 'boards'],
        'boards-panel': ['boards-panel', 'boards'],
        paths: ['paths'],
        adjustments: ['adjustments'],
        character: ['character-panel', 'character'],
        'character-panel': ['character-panel', 'character'],
        paragraph: ['paragraph-panel', 'paragraph'],
        'paragraph-panel': ['paragraph-panel', 'paragraph'],
        opentype: ['opentype-panel', 'opentype'],
        'opentype-panel': ['opentype-panel', 'opentype'],
        'typography-settings': ['typography-settings'],
        autolayout: ['autolayout-panel', 'autolayout'],
        'copy-generation': ['copy-generation'],
        translation: ['translation'],
        search: ['semantic-search', 'search'],
        'semantic-search': ['semantic-search', 'search'],
        'design-audit': ['design-audit'],
        accessibility: ['accessibility'],
        automation: ['automation'],
        workboard: ['workboard'],
        'model-library': ['model-library'],
        'download-manager': ['download-manager'],
        'model-loader': ['model-loader'],
        'inference-settings': ['inference-settings'],
        'training-progress': ['training-progress'],
        'cloud-settings': ['cloud-settings'],
        'user-list': ['user-list'],
        'sync-status': ['sync-status'],
        'version-history': ['version-history'],
        'diff-viewer': ['diff-viewer'],
        'user-management': ['user-management'],
        'role-management': ['role-management'],
        comments: ['comments-panel', 'comments'],
        'comments-panel': ['comments-panel', 'comments'],
        timeline: ['timeline'],
        'morph-editor': ['morph-editor'],
        'morph-timeline': ['morph-timeline'],
        physics: ['physics'],
        'particle-editor': ['particle-editor'],
        'shader-graph-editor': ['shader-graph-editor'],
        'filter-library': ['filter-library'],
        'material-editor': ['material-editor'],
        'lighting-controls': ['lighting-controls'],
        performance: ['performance'],
        'gpu-acceleration': ['gpu-acceleration'],
        'performance-comparison': ['performance-comparison'],
        optimization: ['optimization'],
        'privacy-settings': ['privacy-settings'],
        prototype: ['prototype'],
        minimap: ['minimap'],
        'webxr-preview': ['webxr-preview'],
    };

    const matchesTab = (tabId: string, requestedPanelId: string): boolean => {
        if (tabId === requestedPanelId) return true;
        const aliases = TAB_ALIASES[requestedPanelId] ||
            TAB_ALIASES[tabId] || [requestedPanelId];
        return aliases.some(
            (alias) =>
                tabId === alias ||
                tabId === `${panelPosition}-${alias}` ||
                tabId.endsWith(`-${alias}`),
        );
    };

    const togglePanelVisibility = (panelId: string) => {
        // Menu commands, menu actions and internal calls use different id
        // variants for the same panel; normalize once so flags, events and
        // lookups all operate on one canonical id.
        const canonicalId = toCanonicalPanelId(panelId);
        console.log('togglePanelVisibility executing for:', canonicalId);
        // Ensure sidepanel is not collapsed
        if (isCollapsedRef.current) {
            setIsCollapsed(false);
            if (isHorizontal) {
                setHeight(lastHeight || defaultHeight);
            } else {
                setWidth(lastWidth || defaultWidth);
            }
        }
        // Ensure sidepanel is not minimized
        if (isMinimizedRef.current) {
            setIsMinimized(false);
            if (isHorizontal) {
                setHeight(lastHeight || defaultHeight);
            } else {
                setWidth(lastWidth || defaultWidth);
            }
        }

        // Decide synchronously from the current groups so visibility flags and
        // events are set within the same event tick (other instances listen to
        // the same events and must not resurrect a panel being hidden).
        const current = panelGroups || [];

        if (current.length === 0) {
            console.log(
                'togglePanelVisibility: panelGroups is empty, requesting tab restore for:',
                canonicalId,
            );
            // Only the primary shell may rebuild tabs: if dynamic instances
            // also broadcast this, every mounted instance re-adds the tab and
            // panels come back in pairs.
            if (panelId === 'main-panel') {
                window.dispatchEvent(
                    new CustomEvent(PANEL_EVENTS.requestRestoreTab, {
                        detail: { panelId: canonicalId },
                    }),
                );
            }
            return;
        }

        // Find the group containing the requested panel as a tab. Toggles are
        // tab-scoped: group ids must never match here, otherwise toggling one
        // panel inside a multi-tab group hides the whole group's other panels.
        const groupWithTab = current.find((group) =>
            group.tabs.some((tab) => matchesTab(tab.id, canonicalId)),
        );

        if (groupWithTab) {
            const tab = groupWithTab.tabs.find((t) =>
                matchesTab(t.id, canonicalId),
            );
            if (tab) {
                const isCurrentlyActive = activeTabRef.current === tab.id;
                // Visibility is presence in a shown group, matching exactly
                // what the Window menu checkmark reports through
                // useSidePanelStore.visiblePanels (every tab of a visible
                // group, including background tabs). Tying it to the single
                // shell-wide activeTab instead made every non-focused panel
                // toggle fall into the activate branch and look dead.
                const isPanelCurrentlyVisible = groupWithTab.isVisible;
                if (isPanelCurrentlyVisible) {
                    // Toggle OFF: remove ONLY this tab from its group; sibling
                    // tabs stay open. If it was the last tab, the group
                    // disappears (and the shell auto-hides when no groups left).
                    console.log(
                        'togglePanelVisibility: removing tab',
                        tab.id,
                        'from group',
                        groupWithTab.id,
                    );
                    markPanelHidden(canonicalId);
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.visibilityChange, {
                            detail: { panelId: canonicalId, visible: false },
                        }),
                    );
                    setPanelGroups(
                        current
                            .map((group) =>
                                group.id === groupWithTab.id
                                    ? {
                                          ...group,
                                          tabs: group.tabs.filter(
                                              (t) => t.id !== tab.id,
                                          ),
                                      }
                                    : group,
                            )
                            .filter((group) => group.tabs.length > 0),
                    );
                    if (isCurrentlyActive) {
                        const remainingGroup = current.find(
                            (g) =>
                                g.id === groupWithTab.id && g.tabs.length > 1,
                        );
                        const nextActive = remainingGroup?.tabs.find(
                            (t) => t.id !== tab.id,
                        );
                        setActiveTab(nextActive ? nextActive.id : '');
                    }
                } else {
                    // Toggle ON: clear the hidden flag first, then re-add the
                    // tab and activate it.
                    console.log(
                        'togglePanelVisibility: activating tab',
                        tab.id,
                        'in group',
                        groupWithTab.id,
                    );
                    markPanelVisible(canonicalId);
                    setActiveTab(tab.id);
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.visibilityChange, {
                            detail: { panelId: canonicalId, visible: true },
                        }),
                    );
                    setPanelGroups(
                        current.map((group) =>
                            group.id === groupWithTab.id
                                ? { ...group, isVisible: true }
                                : group,
                        ),
                    );
                }
            }
            return;
        }

        console.log(
            'togglePanelVisibility: tab not found in panelGroups, dispatching requestRestoreTab for:',
            canonicalId,
        );
        // Tab is not currently in any group (may have been closed or not yet
        // added). Again, only the primary shell may rebuild tabs.
        if (panelId === 'main-panel') {
            window.dispatchEvent(
                new CustomEvent(PANEL_EVENTS.requestRestoreTab, {
                    detail: { panelId: canonicalId },
                }),
            );
        }
    };
    const handleCloseTab = (tabId: string, groupId: string) => {
        // Mirror the Window-menu toggle semantics: mark the panel hidden so
        // MenuBar checkmarks and restore guards stay consistent when the user
        // closes a tab with the X button. Flags live under the canonical id
        // with the instance prefix stripped — the same spelling menu toggles
        // and the app-side visibility sync use.
        const closedTab = (panelGroups || [])
            .find((g) => g.id === groupId)
            ?.tabs.find((t) => t.id === tabId);
        if (closedTab) {
            const strippedTabId = closedTab.id.startsWith(`${panelId}-`)
                ? closedTab.id.slice(panelId.length + 1)
                : closedTab.id;
            markPanelHidden(toCanonicalPanelId(strippedTabId));
            window.dispatchEvent(
                new CustomEvent(PANEL_EVENTS.visibilityChange, {
                    detail: {
                        panelId: toCanonicalPanelId(strippedTabId),
                        visible: false,
                    },
                }),
            );
        }
        setPanelGroups((prev) => {
            const newGroups = prev.map((group) => {
                if (group.id === groupId) {
                    const updatedTabs = group.tabs.filter(
                        (tab) => tab.id !== tabId,
                    );
                    return { ...group, tabs: updatedTabs };
                }
                return group;
            });
            const filteredGroups = newGroups.filter(
                (group) => group.tabs.length > 0,
            );
            if (tabId === activeTab && filteredGroups.length > 0) {
                const currentGroup = newGroups.find(
                    (g) => g.id === groupId && g.tabs.length > 0,
                );
                if (currentGroup && currentGroup.tabs.length > 0) {
                    setActiveTab(currentGroup.tabs[0].id);
                } else if (filteredGroups[0]?.tabs[0]) {
                    setActiveTab(filteredGroups[0].tabs[0].id);
                }
            } else if (filteredGroups.length === 0) {
                setActiveTab('');
            }
            return filteredGroups;
        });
    };
    const closeFloatingWindow = (windowId: string) => {
        const window = floatingWindows.find((w) => w.id === windowId);
        if (!window) return;
        if ((panelGroups ?? []).length > 0) {
            setPanelGroups((prev) => {
                const newGroups = [...prev];
                const firstGroup = {
                    ...newGroups[0],
                    tabs: [...newGroups[0].tabs, window.tab],
                };
                newGroups[0] = firstGroup;
                return newGroups;
            });
            setActiveTab(window.tab.id);
        } else {
            // No group left in this shell (the last tab was floated out):
            // closing the window must re-dock the tab, not destroy it —
            // recreate a minimized group to receive it.
            setPanelGroups(() => [
                {
                    id: generateUniqueId(),
                    isVisible: true,
                    isMinimized: true,
                    height: 40,
                    tabs: [window.tab],
                },
            ]);
            setActiveTab(window.tab.id);
        }
        setFloatingWindows((prev) => prev.filter((w) => w.id !== windowId));
    };
    const moveFloatingWindow = (windowId: string, x: number, y: number) => {
        setFloatingWindows((prev) =>
            prev.map((w) =>
                w.id === windowId ? { ...w, position: { x, y } } : w,
            ),
        );
    };
    const resizeFloatingWindow = (
        windowId: string,
        width: number,
        height: number,
    ) => {
        setFloatingWindows((prev) =>
            prev.map((w) =>
                w.id === windowId ? { ...w, size: { width, height } } : w,
            ),
        );
    };
    const setFloatingWindowDragging = (
        windowId: string,
        isDragging: boolean,
    ) => {
        setFloatingWindows((prev) =>
            prev.map((w) => (w.id === windowId ? { ...w, isDragging } : w)),
        );
    };
    const setFloatingWindowResizing = (
        windowId: string,
        isResizing: boolean,
    ) => {
        setFloatingWindows((prev) =>
            prev.map((w) => (w.id === windowId ? { ...w, isResizing } : w)),
        );
    };
    const toggleGroupMinimize = (groupId: string) => {
        setPanelGroups((prev) =>
            prev.map((group) =>
                group.id === groupId
                    ? {
                          ...group,
                          isMinimized: !group.isMinimized,
                          isAutoCollapsed: false,
                          height: group.isMinimized ? defaultHeight : 40,
                      }
                    : group,
            ),
        );
    };
    const handleDragStart = (
        e: React.DragEvent,
        tabId: string,
        groupId: string,
    ) => {
        e.dataTransfer.setData(
            'text/plain',
            JSON.stringify({ tabId, groupId }),
        );
        (e.target as HTMLElement).classList.add('dragging');
        setDraggedTab({ tabId, groupId });
    };
    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        target.classList.remove('dragging');
        setActiveDropZone(null);
        setIsDraggingOutside(false);
        setDraggedTab(null);
    };
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        const target = e.target as HTMLElement;
        if (target.classList.contains('panel-tab')) {
            target.classList.add('drop-target');
        }
        setIsDraggingOutside(false);
    };
    const handleDragLeave = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('panel-tab')) {
            target.classList.remove('drop-target');
        }
    };
    const handleDropZoneDragOver = (e: React.DragEvent, zoneId: string) => {
        e.preventDefault();
        setActiveDropZone(zoneId);
        setIsDraggingOutside(false);
    };
    const handleDropZoneDragLeave = (_e: React.DragEvent) => {
        setActiveDropZone(null);
    };
    const handleDrop = (
        e: React.DragEvent,
        targetTabId: string,
        targetGroupId: string,
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const dataString = e.dataTransfer.getData('text/plain');
        if (!dataString) return;
        let data: any;
        try {
            data = JSON.parse(dataString);
        } catch {
            return;
        }
        const { tabId: sourceTabId, groupId: sourceGroupId } = data;
        if (sourceGroupId === 'floating') {
            const windowDataString = e.dataTransfer.getData(
                'application/floating-window',
            );
            if (windowDataString) {
                const windowData = JSON.parse(windowDataString);
                const { windowId } = windowData;
                setFloatingWindows((prev) =>
                    prev.filter((w) => w.id !== windowId),
                );
            }
        }
        if (sourceGroupId === targetGroupId && sourceTabId === targetTabId)
            return;
        setPanelGroups((prev) => {
            const newGroups = prev.map((group) => ({
                ...group,
                tabs: [...group.tabs],
            }));
            let sourceTab: TabConfig | undefined;
            if (sourceGroupId === 'floating') {
                const windowDataString = e.dataTransfer.getData(
                    'application/floating-window',
                );
                if (windowDataString) {
                    const windowData = JSON.parse(windowDataString);
                    const floatingWindow = floatingWindows.find(
                        (w) => w.id === windowData.windowId,
                    );
                    if (floatingWindow) {
                        sourceTab = floatingWindow.tab;
                    }
                }
            } else {
                const sourceGroup = newGroups.find(
                    (g) => g.id === sourceGroupId,
                );
                if (sourceGroup) {
                    const sourceIndex = sourceGroup.tabs.findIndex(
                        (t) => t.id === sourceTabId,
                    );
                    if (sourceIndex !== -1) {
                        sourceTab = { ...sourceGroup.tabs[sourceIndex] };
                        sourceGroup.tabs = sourceGroup.tabs.filter(
                            (t) => t.id !== sourceTabId,
                        );
                    }
                }
            }
            if (!sourceTab) return prev;
            // A const (not the captured `let`) keeps the setFloatingWindows
            // closure below narrowed to a defined tab.
            const detachedTab = sourceTab;
            if (!targetTabId || !targetGroupId) {
                setFloatingWindows((prev) => [
                    ...prev,
                    {
                        id: generateUniqueId(),
                        tab: detachedTab,
                        position: { x: e.clientX, y: e.clientY },
                        size: { width: 300, height: 200 },
                        isResizing: false,
                        isDragging: false,
                    },
                ]);
                return newGroups.filter((group) => group.tabs.length > 0);
            }
            const targetGroup = newGroups.find((g) => g.id === targetGroupId);
            if (!targetGroup) return prev;
            const targetIndex = targetGroup.tabs.findIndex(
                (t) => t.id === targetTabId,
            );
            if (targetIndex === -1) {
                targetGroup.tabs.push(sourceTab);
            } else {
                targetGroup.tabs.splice(targetIndex, 0, sourceTab);
            }
            setActiveTab(sourceTab.id);
            return newGroups.filter((group) => group.tabs.length > 0);
        });
    };
    const handleDropOnList = (e: React.DragEvent, groupId: string) => {
        e.preventDefault();
        e.stopPropagation();
        const dataString = e.dataTransfer.getData('text/plain');
        if (!dataString) return;
        let data: any;
        try {
            data = JSON.parse(dataString);
        } catch {
            return;
        }
        const { tabId: sourceTabId, groupId: sourceGroupId } = data;
        if (sourceGroupId === 'floating') {
            const windowDataString = e.dataTransfer.getData(
                'application/floating-window',
            );
            if (windowDataString) {
                const windowData = JSON.parse(windowDataString);
                const { windowId } = windowData;
                const floatingWindow = floatingWindows.find(
                    (w) => w.id === windowId,
                );
                if (floatingWindow) {
                    const existingTabIds = (panelGroups ?? []).flatMap(
                        (group) => group.tabs.map((tab) => tab.id),
                    );
                    let newTabId = floatingWindow.tab.id;
                    if (existingTabIds.includes(newTabId)) {
                        newTabId = `${newTabId}-${generateUniqueId()}`;
                    }
                    const updatedTab = { ...floatingWindow.tab, id: newTabId };
                    setFloatingWindows((prev) =>
                        prev.filter((w) => w.id !== windowId),
                    );
                    setPanelGroups((prev) => {
                        const newGroups = prev.map((group) => ({
                            ...group,
                            tabs: [...group.tabs],
                        }));
                        const targetGroup = newGroups.find(
                            (g) => g.id === groupId,
                        );
                        if (targetGroup) {
                            targetGroup.tabs.push(updatedTab);
                        }
                        return newGroups;
                    });
                }
            }
        }
    };
    const handleDropOnZone = (
        e: React.DragEvent,
        targetGroupId: string,
        position: 'before' | 'after',
    ) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveDropZone(null);
        const dataString = e.dataTransfer.getData('text/plain');
        if (!dataString) return;
        let data: any;
        try {
            data = JSON.parse(dataString);
        } catch {
            return;
        }
        const { tabId: sourceTabId, groupId: sourceGroupId } = data;
        setTimeout(() => {
            if (sourceGroupId === 'floating') {
                const windowDataString = e.dataTransfer.getData(
                    'application/floating-window',
                );
                if (windowDataString) {
                    const windowData = JSON.parse(windowDataString);
                    const { windowId } = windowData;
                    setFloatingWindows((prev) =>
                        prev.filter((w) => w.id !== windowId),
                    );
                }
            }
            setPanelGroups((prev) => {
                const newGroups = prev.map((group) => ({
                    ...group,
                    tabs: [...group.tabs],
                }));
                let sourceTab: TabConfig | undefined;
                if (sourceGroupId === 'floating') {
                    const windowDataString = e.dataTransfer.getData(
                        'application/floating-window',
                    );
                    if (windowDataString) {
                        const windowData = JSON.parse(windowDataString);
                        const floatingWindow = floatingWindows.find(
                            (w) => w.id === windowData.windowId,
                        );
                        if (floatingWindow) {
                            sourceTab = floatingWindow.tab;
                        }
                    }
                } else {
                    const sourceGroup = newGroups.find(
                        (g) => g.id === sourceGroupId,
                    );
                    if (sourceGroup) {
                        const sourceIndex = sourceGroup.tabs.findIndex(
                            (t) => t.id === sourceTabId,
                        );
                        if (sourceIndex !== -1) {
                            sourceTab = { ...sourceGroup.tabs[sourceIndex] };
                            sourceGroup.tabs = sourceGroup.tabs.filter(
                                (t) => t.id !== sourceTabId,
                            );
                        }
                    }
                }
                if (!sourceTab) return prev;
                const newGroup: PanelGroup = {
                    id: generateUniqueId(),
                    isVisible: true,
                    height: defaultHeight,
                    tabs: [sourceTab],
                };
                const targetGroupIndex = newGroups.findIndex(
                    (g) => g.id === targetGroupId,
                );
                if (targetGroupIndex === -1) return prev;
                const insertAtIndex =
                    position === 'before'
                        ? targetGroupIndex
                        : targetGroupIndex + 1;
                newGroups.splice(insertAtIndex, 0, newGroup);
                setTimeout(() => setActiveTab(sourceTab!.id), 0);
                return newGroups.filter((group) => group.tabs.length > 0);
            });
        }, 0);
    };
    const handleTabDrop = (
        e: React.DragEvent,
        targetTabId: string,
        targetGroupId: string,
        position: 'left' | 'right',
        setTabDropPosition: React.Dispatch<
            React.SetStateAction<Record<string, 'left' | 'right' | null>>
        >,
        setFloatingWindowsExternal?: React.Dispatch<
            React.SetStateAction<FloatingWindow[]>
        >,
    ) => {
        e.preventDefault();
        e.stopPropagation();
        document
            .querySelectorAll(
                '.panel-tab.drop-target-left, .panel-tab.drop-target-right',
            )
            .forEach((el) => {
                el.classList.remove('drop-target-left', 'drop-target-right');
            });
        const target = e.target as HTMLElement;
        target.classList.remove(
            'drop-target-left',
            'drop-target-right',
            'dragging',
        );
        const dataString = e.dataTransfer.getData('text/plain');
        if (!dataString) return;
        let data: any;
        try {
            data = JSON.parse(dataString);
        } catch {
            return;
        }
        const { tabId: sourceTabId, groupId: sourceGroupId } = data;
        setTabDropPosition((pos) => ({ ...pos, [targetTabId]: null }));
        if (sourceGroupId === targetGroupId && sourceTabId === targetTabId)
            return;
        setPanelGroups((prev) => {
            const newGroups = prev.map((group) => ({
                ...group,
                tabs: [...group.tabs],
            }));
            let sourceTab: TabConfig | undefined;
            if (sourceGroupId === 'floating') {
                const windowDataString = e.dataTransfer.getData(
                    'application/floating-window',
                );
                if (windowDataString) {
                    const windowData = JSON.parse(windowDataString);
                    const floatingWindow = floatingWindows.find(
                        (w) => w.id === windowData.windowId,
                    );
                    if (floatingWindow) {
                        const targetGroup = newGroups.find(
                            (g) => g.id === targetGroupId,
                        );
                        const existingTabIds =
                            targetGroup?.tabs.map((t) => t.id) || [];
                        let newTabId = floatingWindow.tab.id;
                        if (existingTabIds.includes(newTabId)) {
                            newTabId = `${newTabId}-${generateUniqueId()}`;
                        }
                        sourceTab = { ...floatingWindow.tab, id: newTabId };
                        if (setFloatingWindowsExternal) {
                            setFloatingWindowsExternal((prevFW) =>
                                prevFW.filter(
                                    (w) => w.id !== windowData.windowId,
                                ),
                            );
                        } else {
                            setFloatingWindows((prevFW) =>
                                prevFW.filter(
                                    (w) => w.id !== windowData.windowId,
                                ),
                            );
                        }
                    }
                }
            } else {
                const sourceGroup = newGroups.find(
                    (g) => g.id === sourceGroupId,
                );
                if (sourceGroup) {
                    const sourceIndex = sourceGroup.tabs.findIndex(
                        (t) => t.id === sourceTabId,
                    );
                    if (sourceIndex !== -1) {
                        sourceTab = { ...sourceGroup.tabs[sourceIndex] };
                        sourceGroup.tabs = sourceGroup.tabs.filter(
                            (t) => t.id !== sourceTabId,
                        );
                    }
                }
            }
            const targetGroup = newGroups.find((g) => g.id === targetGroupId);
            if (!sourceTab || !targetGroup) return prev;
            const targetIndex = targetGroup.tabs.findIndex(
                (t) => t.id === targetTabId,
            );
            if (targetIndex === -1) {
                targetGroup.tabs.push(sourceTab);
            } else {
                if (position === 'left') {
                    targetGroup.tabs.splice(targetIndex, 0, sourceTab);
                } else {
                    targetGroup.tabs.splice(targetIndex + 1, 0, sourceTab);
                }
            }
            setActiveTab(sourceTab.id);
            return newGroups.filter((group) => group.tabs.length > 0);
        });
    };
    const togglePanelVisibilityRef = useRef(togglePanelVisibility);
    useEffect(() => {
        togglePanelVisibilityRef.current = togglePanelVisibility;
    });

    useEffect(() => {
        const handleTogglePanel = (event: Event) => {
            console.log('togglePanel event received in useSidePanel:', event);
            const customEvent = event as CustomEvent<{ panelId: string }>;
            console.log('CustomEvent detail:', customEvent.detail);
            if (customEvent.detail?.panelId) {
                togglePanelVisibilityRef.current(customEvent.detail.panelId);
            }
        };
        // activatePanelTab is the restore-flow companion of togglePanel: it
        // shows the tab if it is not already shown and never toggles it off,
        // so a queued activation cannot close a panel the user just opened.
        const handleActivatePanel = (event: Event) => {
            const customEvent = event as CustomEvent<{ panelId: string }>;
            const canonicalId = customEvent.detail?.panelId;
            if (!canonicalId) return;
            // Read the live groups snapshot: this listener is registered once
            // on mount, so a stale closure here would never see tabs added by
            // later restores and would re-request them endlessly.
            const current = panelGroupsRef.current || [];
            const groupWithTab = current.find((group) =>
                group.tabs.some((tab) => matchesTab(tab.id, canonicalId)),
            );
            const tab = groupWithTab?.tabs.find((t) =>
                matchesTab(t.id, canonicalId),
            );
            if (groupWithTab && tab) {
                markPanelVisible(canonicalId);
                setActiveTab(tab.id);
                window.dispatchEvent(
                    new CustomEvent(PANEL_EVENTS.visibilityChange, {
                        detail: { panelId: canonicalId, visible: true },
                    }),
                );
                setPanelGroups(
                    current.map((group) =>
                        group.id === groupWithTab.id
                            ? { ...group, isVisible: true }
                            : group,
                    ),
                );
            } else {
                // Only the primary shell may broadcast the rebuild request;
                // dynamic instances doing this too is what resurrected
                // detached panels in duplicate.
                if (panelId === 'main-panel') {
                    window.dispatchEvent(
                        new CustomEvent(PANEL_EVENTS.requestRestoreTab, {
                            detail: { panelId: canonicalId },
                        }),
                    );
                }
            }
        };
        console.log('Registering togglePanel event listener');
        window.addEventListener(PANEL_EVENTS.togglePanel, handleTogglePanel);
        window.addEventListener(PANEL_EVENTS.activateTab, handleActivatePanel);
        return () => {
            console.log('Removing togglePanel event listener');
            window.removeEventListener(PANEL_EVENTS.togglePanel, handleTogglePanel);
            window.removeEventListener(PANEL_EVENTS.activateTab, handleActivatePanel);
        };
    }, []);
    return {
        width,
        height,
        isCollapsed,
        isMinimized,
        activeTab,
        isResizing,
        activeDropZone,
        floatingWindows,
        isDraggingOutside,
        draggedTab,
        panelGroups: panelGroups || initialPanelGroups,
        setWidth,
        setHeight,
        setIsCollapsed,
        setActiveTab,
        toggleCollapse,
        toggleMinimized,
        createPanelResizeHandleMouseDown,
        createGroupResizeHandleMouseDown,
        togglePanelVisibility,
        handleCloseTab,
        closeFloatingWindow,
        moveFloatingWindow,
        resizeFloatingWindow,
        setFloatingWindowDragging,
        setFloatingWindowResizing,
        handleDragStart,
        handleDragEnd,
        handleDragOver,
        handleDragLeave,
        handleDropZoneDragOver,
        handleDropZoneDragLeave,
        handleDrop,
        handleDropOnList,
        handleDropOnZone,
        handleTabDrop,
        setFloatingWindows,
        setPanelGroups,
        setIsDraggingOutside,
        setDraggedTab,
        toggleGroupMinimize,
        hydrating,
    };
}
